import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface IntegracaoWppData {
    id: number | null
    wabaid: string
    conta: string
    telefone: string
    ativo: boolean
    qualityRating?: string
    verificationStatus?: string
    phoneNumber?: string
    verifiedName?: string
    webhook?: string
}

export interface SalvarIntegracaoPayload {
    wabaid: string
    ativo: boolean
}

export interface TemplateData {
    id: string
    name: string
    parameter_format: string
    status: string
    category: string
    language: string
}

export interface TemplatesResponse {
    data: TemplateData[]
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const obterIntegracaoWpp = async function (
    authorization: string
): Promise<IntegracaoWppData | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<IntegracaoWppData>>("whatsapp/ObterIntegracaoWpp", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<IntegracaoWppData>(response, "ObterIntegracaoWpp")
    } catch (error) {
        RegistrarLogErro(error, "ObterIntegracaoWpp", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarIntegracaoWpp = async function (
    authorization: string,
    payload: SalvarIntegracaoPayload
): Promise<IntegracaoWppData | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<IntegracaoWppData>>("whatsapp/SalvarIntegracaoWpp", payload, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<IntegracaoWppData>(response, "SalvarIntegracaoWpp")
    } catch (error) {
        RegistrarLogErro(error, "SalvarIntegracaoWpp", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirTemplateWpp = async function (
    authorization: string,
    nomeTemplate: string
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<{ codErro?: number; mensagem?: string } | null>(
                `whatsapp/ExcluirTemplate/${encodeURIComponent(nomeTemplate)}`,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return {
                    data: erroParaRespostaRaw(error) as { codErro?: number; mensagem?: string },
                }
            })

        const d = response.data
        if (d && typeof d === "object" && d.codErro !== undefined && d.codErro > 0) {
            return { retornoErro: d.codErro, mensagem: d.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ExcluirTemplate", nomeTemplate)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export interface SituacaoTemplateConfirmacao {
    whatsappOficialAtivo: boolean
    existe: boolean
    nome: string | null
    status: string | null
    motivoRejeicao: string | null
    dominioDivergente: boolean
    mensagem: string
}

/** Cria o template de confirmação do pedido na WABA da loja. Idempotente: se já existe
 *  aprovado ou em análise, só devolve a situação. Os erros desse endpoint vêm como { erro }. */
export const criarTemplateConfirmacaoPedido = async function (
    authorization: string
): Promise<SituacaoTemplateConfirmacao | ErroApi> {
    try {
        const response = await api.post<SituacaoTemplateConfirmacao>(
            "whatsapp/template-confirmacao-pedido",
            null,
            { headers: { "Content-Type": "application/json", Authorization: authorization } }
        )
        return response.data
    } catch (error) {
        const data = (error as { response?: { data?: { erro?: string } } }).response?.data
        if (data && typeof data === "object" && data.erro) {
            return { retornoErro: 1, mensagem: data.erro }
        }
        RegistrarLogErro(error, "criarTemplateConfirmacaoPedido", "")
        return { retornoErro: 1, mensagem: erroParaRespostaRaw(error).mensagem ?? "" }
    }
}

type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

export const listarTemplatesWpp = async function (
    authorization: string
): Promise<TemplateData[] | ErroApi> {
    try {
        const response = await api
            .get<TemplatesResponse | RespostaErroRaw>("whatsapp/templates", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        const templatesResp = response.data as TemplatesResponse
        return templatesResp.data ?? []
    } catch (error) {
        RegistrarLogErro(error, "listarTemplates", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
